import { observable, action, computed } from "mobx";
import toastService from "../services/ToastService";
import validationService from "../services/ValidationService";
import { 
  FILE_FORMAT,
  VALIDATION_TYPE,
 } from "src/constants/constants-container"


export default class ValidationsStore {

  @observable
  validationsModalVisibilityMap = observable.map(
    {
      "cm": false,
      "da": false,
      "po": false,
      "mao": false,
    }
  );
  @observable validationResults = [];
  @observable latestValidationResultsCount = null;
  @observable latestRequestParams = {
    tids: null,
    format: null,
    offset: 0,
    limit: 10,
    requestId: null,
    payload: {},
    validationType: null,
  }

  validatedReleaseName = "";
  validatedFrameworkName = "";

  @observable ongoingRequestId = null;

  @action
  runMultipleValidations = (parent, tids, shouldValidateAll, entityType, validationType, trackingParams) => {
    const tracking = trackingParams ? trackingParams : {};
    tracking.viewLabel = parent;
    
    if(shouldValidateAll) {
      this.triggerValidations(null, entityType, null, validationType, tracking);
      this.toggleValidationsModalVisibility(parent);
    } else {
      if (tids.length > 0) {
        this.triggerValidations(tids, entityType, null, validationType, tracking);
        this.toggleValidationsModalVisibility(parent);
      } else {
        toastService.func_notify(
          "Please select entities to be validated.",
          "error"
        );
      }
    }
  }

  validateRelease = (parent, release, frameworkName, project) => {
    this.validatedFrameworkName = frameworkName;
    this.validatedReleaseName = release.name;
    const payload = {
      entityFilter: {
        type: "Entity",
        bid: {
          in: [release.tid]
        },
        businessId: null,
        approvalStatus: {
          in:["APPROVED"]
        },
        coreApprovalStatus: null,
        typeOfBranch: null
      },
      linkSuperiorBranches: project.projectType === "FRAMEWORK" ? false : true,
      entityTypes: []
    };

    const trackingParams = {
      pid: project.tid,
      bid: release.tid
    }

    trackingParams.viewLabel = parent
    this.triggerValidations(null, null, payload, VALIDATION_TYPE.RELEASE, trackingParams);
    this.toggleValidationsModalVisibility(parent);
  }

  exportToExcel = async () => {
    await validationService.exportValidationResults(this.latestRequestParams.requestId, FILE_FORMAT.XLSX.name)
  }

  @action
  fetchValidationResults = (requestId, offset, limit) => {
    const offsetToUse = offset ?? this.latestRequestParams.offset
    const limitToUse = limit ?? this.latestRequestParams.limit

    validationService.fetchValidationResults(requestId, offsetToUse, limitToUse).then(
      action(response => {
        if (response.validationResults && response.validationResults.entityValidationResults) {
          this.latestRequestParams.requestId = response.requestId;
          this.latestValidationResultsCount = response.count;

          const valResults = [];
          for (let i = 0; i < response.validationResults.entityValidationResults.length; i++) {
            for (let j = 0; j < response.validationResults.entityValidationResults[i].errors.length; j++) {
              valResults.push(
                {
                  objectType: response.validationResults.entityValidationResults[i].type || "N/A",
                  objectId: response.validationResults.entityValidationResults[i].businessIdValue || "N/A",
                  severity: response.validationResults.entityValidationResults[i].errors[j].level || "N/A",
                  message: response.validationResults.entityValidationResults[i].errors[j].message || "N/A"
                }
              );
            }
          }
          this.validationResults = valResults;
        } else if (response?.status === 500) {
          toastService.func_notify(
            "Failed to query validation results. Reason: " + response.message,
            "error"
          );
        }
      })
    )
  }

  @action
  triggerValidations = (tids, entityType, payload, validationType, trackingParams) => {
    const payloadToUse = !!payload ? payload : this.getPayload(tids, entityType, validationType);
    this.enrichPayload(payloadToUse, validationType, trackingParams)

    this.latestRequestParams.tids = tids;
    this.latestRequestParams.payload = payloadToUse;
    this.latestRequestParams.validationType = validationType;

    validationService.triggerValidations(payloadToUse).then(
      action(requestId => {
        if (requestId) {
          this.latestRequestParams.requestId = requestId;
          this.ongoingRequestId = requestId
        }
      })
    )
  }

  @action
  changeTableSettings = (state, instance) => {
    this.latestRequestParams.limit = state.pageSize;
    this.latestRequestParams.offset = this.latestRequestParams.limit * state.page;
  }

  @computed
  get getLatestRequestParams() {
    return this.latestRequestParams;
  }

  getPageCount = () => {
    return computed(
      () =>
        this.latestValidationResultsCount === null ||
        this.latestValidationResultsCount === -1
          ? -1
          : Math.ceil(this.latestValidationResultsCount / this.latestRequestParams.limit)
    ).get();
  };

  @action
  toggleValidationsModalVisibility = parent => {
    this.validationsModalVisibilityMap.set(
      parent, !this.validationsModalVisibilityMap.get(parent)
    );
    this.resetValidationResults();
  };

  @action
  resetValidationResults = () => {
    this.validationResults = [];
  }

  isValidationsModalVisible = parent => {
    return computed(() => this.validationsModalVisibilityMap.get(parent)).get();
  }

  @computed
  get getValidationResults() {
    return this.validationResults;
  }

  getPayload = (tids, entityType, validationType = VALIDATION_TYPE.GENERAL) => {
    let payload = {};

    if (tids) {
      payload.entityTids = tids;
    } else {
      let lastUsedFilter = {};
      if(this.doesValidationTypeNeedLastApprovalChangeQuery(validationType) && validationService.getLastApprovalChangesQueryByValidationIssuer().has(validationType.issuer)){
        lastUsedFilter = validationService.getLastApprovalChangesQueryByValidationIssuer().get(validationType.issuer)
      } else if(validationType === VALIDATION_TYPE.CONTENT_MANAGEMENT_TABLE && !!entityType && validationService.getLastCmQueryByType().has(entityType)) {
        lastUsedFilter = validationService.getLastCmQueryByType().get(entityType)
      }
      payload.entityFilter = lastUsedFilter;
    }

    return payload;
  }

  enrichPayload = (payload, validationType, trackingParams) => {
    payload.validationIssuer = validationType.issuer
    payload.trackingParams = trackingParams
  }

  doesValidationTypeNeedLastApprovalChangeQuery = (validationType) => {
    return validationType === VALIDATION_TYPE.APPROVAL_CHANGES || 
      validationType === VALIDATION_TYPE.DA_WORKING_BRANCH_CHANGES ||
      validationType === VALIDATION_TYPE.MASTER_BRANCH_CHANGES
  }

  @computed
  get isRequestOngoing() {
    return !!this.ongoingRequestId
  }

  @action
  resetOngoingRequest = () => {
    this.ongoingRequestId = null;
  }
}
